import React, { useCallback, useMemo } from "react";
import { connect } from "react-redux";

import Form from "antd/es/form";
import { FormInstance } from "antd/es/form/hooks/useForm";
import { useIntl } from "react-intl";

import EntityType from "@mapmycustomers/shared/enum/EntityType";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import { OrganizationMetaData } from "@mapmycustomers/shared/types/Organization";

import DefaultColumnsSettings from "@app/scene/settings/type/DefaultColumnsSettings";
import showSaveSettingSuccessfulMessage from "@app/scene/settings/util/showSaveSettingSuccessfulMessage";
import { getDefaultColumns } from "@app/store/iam";
import { updateDefaultColumns } from "@app/store/iam/actions";
import { RootState } from "@app/store/rootReducer";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import FormItems from "./components/FormItems";
import styles from "./DefaultColumns.module.scss";

interface Props {
  defaultColumns: OrganizationMetaData["defaultColumns"];
  form: FormInstance<DefaultColumnsSettings>;
  onHasChanges: () => void;
  onSaved: () => void;
  onUpdateDefaultColumns: typeof updateDefaultColumns;
}

const DefaultColumns: React.FC<Props> = ({
  defaultColumns,
  form,
  onHasChanges,
  onSaved,
  onUpdateDefaultColumns,
}) => {
  const intl = useIntl();

  const handleSubmit = useCallback(() => {
    const formValues = form.getFieldsValue();

    onUpdateDefaultColumns({
      callback: () => {
        showSaveSettingSuccessfulMessage(intl);
        onSaved();
      },
      defaultColumns: formValues.defaultColumns,
    });
  }, [form, intl, onSaved, onUpdateDefaultColumns]);

  const initialValues = useMemo(() => {
    const result: OrganizationMetaData["defaultColumns"] = {};
    ([EntityType.COMPANY, EntityType.PERSON, EntityType.DEAL] as const).forEach((entityType) => {
      // if present in defaultColumns, use that value...
      if (defaultColumns && entityType in defaultColumns) {
        result[entityType] = defaultColumns[entityType];
        return;
      }

      const fieldModel = getFieldModelByEntityType(entityType);
      if (!fieldModel) {
        return; // shouldn't really happen, but...
      }

      // ...otherwise, using a system default columns
      result[entityType] = fieldModel.fields
        .filter((field) =>
          field.hasAnyFeature(FieldFeature.VISIBLE_BY_DEFAULT, FieldFeature.ALWAYS_VISIBLE)
        )
        .map((field) => field.platformName);
    });
    return result;
  }, [defaultColumns]);

  return (
    <Form
      className={styles.container}
      form={form}
      initialValues={{ defaultColumns: initialValues }}
      onFieldsChange={onHasChanges}
      onFinish={handleSubmit}
    >
      <FormItems />
    </Form>
  );
};

const mapStateToProps = (state: RootState) => ({
  defaultColumns: getDefaultColumns(state),
});

const mapDispatchToProps = {
  onUpdateDefaultColumns: updateDefaultColumns,
};

export default connect(mapStateToProps, mapDispatchToProps)(DefaultColumns);
